import { client, log } from ".";
import {
  capturarSnapshotComision,
  capturarSnapshotDesdeVenta,
  resolverMontoCongelado,
  traceSnapshot,
} from "./comisionSnapshot";
import { getClientes } from "./getClientes";
import { getIntermediarios } from "./getIntermediarios";
import { resolverPctsCongelamiento } from "./resolverPctsCongelamiento";

const parseIds = (csv) =>
  (csv || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

// Localiza el padre del snapshot (sale o contrato-mes) y devuelve { id, pct_congelado_en,
// denominador_total } o null. `denominador_total` sirve para detectar si el refresh de facturación
// corrigió una foto stale (el denom cambió).
const fetchPadre = async (origenTipo, saleId, contratoId, mes) => {
  let q = client.from("comision_snapshot").select("id, pct_congelado_en, denominador_total");
  q =
    origenTipo === "sale"
      ? q.eq("origen_tipo", "sale").eq("sale_id", saleId)
      : q.eq("origen_tipo", "contrato_mes").eq("contrato_id", contratoId).eq("mes", mes);
  const { data } = await q;
  return data?.[0] || null;
};

/**
 * Congela el % de intermediación en el padre `comision_snapshot` al facturar (Fase A2b, ver 7.22).
 * Aditivo: NO cambia ningún número mostrado (los reads de la foto son A3). No lanza — si falla, loguea
 * y devuelve un resultado; nunca aborta la facturación que lo invoca.
 *
 * - **Idempotente / sello:** si el padre ya tiene `pct_congelado_en` NOT NULL → foto SELLADA →
 *   no-op (así se distingue la PRIMERA facturación de una re-conciliación/edit posterior; la
 *   re-facturación tras desfacturar la limpia en A4). `pct_congelado_en` NOT NULL ES el sello.
 * - **Refresh + sella (blindaje):** en la PRIMERA facturación (no sellada) se re-captura el desglose
 *   desde la realidad ACTUAL de la venta (edificios/pantallas/pantallastotales de hoy), SOBRESCRIBIENDO
 *   una foto stale si la había (antes solo se creaba `if (!padre)` → dejaba stale). Para contrato-mes
 *   re-captura SÓLO ese mes (no `capturarSnapshotDesdeContrato`, que pisaría meses ya sellados).
 *   Si el refresh corrige una foto stale (cambió el denominador) → `traceSnapshot` (visibilidad).
 * - Resuelve los 4 % con `resolverPctsCongelamiento` y hace un UPDATE atómico de las 6 columnas
 *   (respeta `chk_pct_congelado_coherencia`). Auto-carga clientes/intermediarios si no se pasan
 *   (one-shot al facturar). Resolución por id (regla 10.7).
 */
export const congelarPctIntermediacion = async ({
  origenTipo,
  saleId = null,
  contratoId = null,
  mes = null,
  facturaDate = null,
  clientes = null,
  intermediarios = null,
}) => {
  try {
    // 1. Sello: si ya está sellada (pct_congelado_en NOT NULL) → foto DEFINITIVA → no-op. Esto
    //    distingue la PRIMERA facturación (null) de una re-conciliación/edit posterior (NOT NULL).
    let padre = await fetchPadre(origenTipo, saleId, contratoId, mes);
    if (padre?.pct_congelado_en != null) return { ok: true, accion: "ya-congelado" };
    const denomAntes = padre?.denominador_total ?? null; // para detectar corrección de stale

    // 2. Fila autoritativa + REFRESH del desglose desde la realidad ACTUAL + armar `venta` para %
    let venta;
    let paisVenta;
    let fechaBase;
    if (origenTipo === "sale") {
      const { data: s } = await client.from("sales").select("*").eq("id", saleId).single();
      if (!s) return { ok: false, accion: "sin-venta" };
      venta = s;
      paisVenta = s.pais;
      fechaBase = facturaDate || s.facturafecha;
      // REFRESH SIEMPRE (no sellada, garantizado por el paso 1): sobrescribe una foto stale con el
      // desglose de hoy. El seal-guard de capturarSnapshotComision no bloquea (aún sin sellar).
      await capturarSnapshotDesdeVenta(s, "modificacion");
    } else {
      const { data: c } = await client.from("sales_contratos").select("*").eq("id", contratoId).single();
      if (!c) return { ok: false, accion: "sin-contrato" };
      const mesesArr = c.meses || [];
      const cuotaIdx = mesesArr.findIndex((m) => m.mes === mes);
      const cuota = cuotaIdx >= 0 ? mesesArr[cuotaIdx] : undefined;
      venta = {
        client_id: c.client_id,
        pais: c.pais,
        apx_id: c.apx_id,
        descuento_acuerdo_pct: c.descuento_acuerdo_pct,
        facturafecha: facturaDate || cuota?.facturafecha || null,
      };
      paisVenta = c.pais;
      fechaBase = facturaDate || cuota?.facturafecha;
      // REFRESH SÓLO este mes (aislado): no pisa otros meses ya sellados del contrato.
      const ids = parseIds(cuota?.edificios_ids);
      if (ids.length > 0) {
        const { data: edificios } = await client
          .from("edificios")
          .select("id, pantallas, totems, espera, cowork")
          .in("id", ids);
        const { monto, monedaOrigen, montoOrigen, tcAplicado } = await resolverMontoCongelado(
          { moneda_dolar: c.moneda_dolar, importe: cuota?.importe, facturafecha: cuota?.facturafecha, mes_cierre: mes },
          c.pais,
          cuotaIdx >= 0 ? { ventaTipo: "sales_contratos", ventaRefId: contratoId, ventaRefIndex: cuotaIdx } : null
        );
        await capturarSnapshotComision({
          origen_tipo: "contrato_mes",
          contrato_id: contratoId,
          mes,
          edificios: edificios || [],
          csvs: {
            pantallas_ascensores: cuota?.pantallas_ascensores,
            pantallas_circulacion: cuota?.pantallas_circulacion,
            pantallas_espera: cuota?.pantallas_espera,
            pantallas_cowork: cuota?.pantallas_cowork,
          },
          monto,
          monedaOrigen,
          montoOrigen,
          tcAplicado,
          pais: c.pais,
          motivo: "modificacion",
        });
      }
    }

    // Re-fetch el padre (ahora con la foto FRESCA) y trazar si el refresh corrigió una foto stale.
    padre = await fetchPadre(origenTipo, saleId, contratoId, mes);
    if (!padre) return { ok: false, accion: "sin-padre" }; // venta sin edificios → no hay foto que congelar
    if (denomAntes != null && padre.denominador_total !== denomAntes) {
      traceSnapshot("facturacion_refresh_corrigio_stale", {
        origenTipo,
        saleId,
        contratoId,
        mes,
        denomAntes,
        denomDespues: padre.denominador_total,
      });
    }

    // 3. Resolver los 4 % efectivos al facturar
    const cli = clientes || (await getClientes({ pais: paisVenta }).catch(() => []));
    const inter = intermediarios || (await getIntermediarios({ pais: paisVenta }).catch(() => []));
    const pcts = resolverPctsCongelamiento(venta, cli, inter, fechaBase ? new Date(fechaBase) : new Date());

    // 3b. Líder de la cuenta (Chile, tramo 4%/1% de Nicolás — regla 10.287/nueva): se congela en la MISMA
    // captura que sella el %, con el `clientes.ejecutivo` ACTUAL de la cuenta en este instante (`cli` es
    // un ARRAY, no un dict — resolver por id, 10.7). Solo Chile: Perú sigue leyendo en vivo (no se toca).
    const clienteAsociado = (cli || []).find((c) => c.id === venta.client_id);
    const ejecutivoLiderCongelado =
      paisVenta === "Chile" && clienteAsociado?.ejecutivo != null ? parseInt(clienteAsociado.ejecutivo, 10) : null;

    // 4. UPDATE atómico de las 6 columnas (satisface chk_pct_congelado_coherencia) + el líder (Chile)
    const { error } = await client
      .from("comision_snapshot")
      .update({
        pct_agencia_congelado: pcts.agencia,
        pct_apx_congelado: pcts.apx,
        pct_broker_congelado: pcts.broker,
        pct_descuento_congelado: pcts.descuento,
        pct_congelado_en: new Date().toISOString(),
        pct_congelado_motivo: "facturacion",
        ejecutivo_lider_congelado: ejecutivoLiderCongelado,
      })
      .eq("id", padre.id);
    if (error) throw error;
    return { ok: true, accion: "congelado", pcts, ejecutivoLiderCongelado };
  } catch (e) {
    traceSnapshot("congelarPct_error", {
      origenTipo,
      saleId,
      contratoId,
      mes,
      error: String(e?.message || e),
    });
    log.error("congelarPctIntermediacion", e);
    return { ok: false, accion: "error", error: String(e?.message || e) };
  }
};
